import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { useSidebar } from "@repo/blacksmith-ui/sidebar";
import { useKeyedState } from "@/hooks/useKeyedState";
import { useValueChanged } from "@/hooks/useValueChanged";

/** Treats SSR and unavailable storage as dismissed so the popover stays closed. */
function readDismissed(localStorageKey: string): boolean {
  if (typeof window === "undefined") {
    return true;
  }
  try {
    return localStorage.getItem(localStorageKey) === "true";
  } catch {
    return true;
  }
}

/**
 * Custom hook for managing dismissable sidebar popovers with localStorage persistence
 * and collapse-aware open state.
 *
 * @param localStorageKey - The key to use for storing dismissal state in localStorage
 * @param shouldShow - Boolean indicating if the popover should be shown
 * @returns Object with open state, close handler, collapse state, and render flag
 */
export function useDismissableSidebarPopover(localStorageKey, shouldShow) {
  const $ = _c(14);
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const isCollapsed = sidebarState === "collapsed" && !isMobile;
  let t0;
  if ($[0] !== localStorageKey) {
    t0 = () => readDismissed(localStorageKey);
    $[0] = localStorageKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [dismissed, setDismissed] = useKeyedState(localStorageKey, t0);
  if (useValueChanged(isCollapsed) && !isCollapsed) {
    const stored = readDismissed(localStorageKey);
    if (stored !== dismissed) {
      setDismissed(stored);
    }
  }
  let t1;
  let t2;
  if ($[2] !== localStorageKey || $[3] !== setDismissed) {
    t1 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const handleStorageChange = e => {
        if (e.key === localStorageKey) {
          if (e.newValue === "true") {
            setDismissed(true);
          } else {
            if (e.newValue === null) {
              setDismissed(false);
            }
          }
        }
      };
      window.addEventListener("storage", handleStorageChange);
      return () => {
        window.removeEventListener("storage", handleStorageChange);
      };
    };
    t2 = [localStorageKey, setDismissed];
    $[2] = localStorageKey;
    $[3] = setDismissed;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== localStorageKey || $[7] !== setDismissed) {
    t3 = e_0 => {
      e_0.preventDefault();
      e_0.stopPropagation();
      setDismissed(true);
      if (typeof window !== "undefined") {
        try {
          localStorage.setItem(localStorageKey, "true");
        } catch {}
      }
    };
    $[6] = localStorageKey;
    $[7] = setDismissed;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleClose = t3;
  const t4 = shouldShow && !isCollapsed && !dismissed;
  let t5;
  if ($[9] !== handleClose || $[10] !== isCollapsed || $[11] !== shouldShow || $[12] !== t4) {
    t5 = {
      open: t4,
      handleClose,
      isCollapsed,
      shouldRender: shouldShow
    };
    $[9] = handleClose;
    $[10] = isCollapsed;
    $[11] = shouldShow;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}